<script setup lang="ts">
import type { Component, Ref } from 'vue';
import type { FieldTypeFilterConfig } from '#layers/data-model/public';
import type { SegmentedFieldFilter } from './utils/filter-path';
import type { FilterSchemaPrimitiveField } from './utils/filter-schema';
import { cloneDeep, set } from 'lodash-es';
import { computed, onMounted, ref, resolveComponent, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isNil } from '#layers/base/public';
import { isPrimitiveTypeDecimal, isPrimitiveTypeNumber } from '#layers/data-model/public';
import FilterInputComponent from './FilterInputComponent.vue';
import { injectFilterInputContext, injectFilterInputGroupDelegateContext, provideFilterInputContext, provideFilterInputGroupDelegateContext } from './FilterInputContext';

const props = defineProps<{
	primitiveField: FilterSchemaPrimitiveField;
	filterConfig?: FieldTypeFilterConfig;
	typeOptions?: Record<string, unknown>;
	disabled?: boolean;
	readonly?: boolean;
}>();

const emit = defineEmits<{
	remove: [];
}>();

const model = defineModel<SegmentedFieldFilter | null>({ required: true });

// Check if we're being rendered as a delegate (private context already provided)
const delegateContext = injectFilterInputGroupDelegateContext(null);
const isDelegate = delegateContext !== null;

// When delegating, read from private context instead of props
const resolvedModel = isDelegate ? delegateContext!.model : model;
const resolvedPrimitiveField = computed(() => isDelegate ? delegateContext!.primitiveField.value : props.primitiveField);
const resolvedFilterConfig = computed(() => isDelegate ? delegateContext!.filterConfig.value : props.filterConfig);
const resolvedTypeOptions = computed(() => isDelegate ? delegateContext!.typeOptions.value : props.typeOptions);
const resolvedDisabled = computed(() => isDelegate ? delegateContext!.disabled.value : props.disabled);
const resolvedReadonly = computed(() => isDelegate ? delegateContext!.readonly.value : props.readonly);
const isEditable = computed(() => !resolvedReadonly.value && !resolvedDisabled.value);

// Resolve the leaf input component
const existingPublicContext = injectFilterInputContext(null);

const resolvedFilterInput = computed<Component>(() => {
	if (isDelegate && existingPublicContext) return existingPublicContext.FilterInput;
	return props.filterConfig?.input ?? FilterInputComponent;
});

// Only provide contexts when we're the primary (not a delegate)
if (!isDelegate) {
	provideFilterInputContext({
		get FilterInput() {
			return resolvedFilterInput.value;
		},
		FilterInputGroup: resolveComponent('FilterInputGroup') as Component,
	});

	provideFilterInputGroupDelegateContext({
		primitiveField: computed(() => props.primitiveField),
		filterConfig: computed(() => props.filterConfig),
		typeOptions: computed(() => props.typeOptions ?? {}),
		model: model as Ref<SegmentedFieldFilter | null>,
		disabled: computed(() => props.disabled ?? false),
		readonly: computed(() => props.readonly ?? false),
	});
}

const { t } = useI18n();
interface FocusableFilterInput {
	focusFirstInput?: () => void;
}

const filterInputRef = useTemplateRef<FocusableFilterInput | FocusableFilterInput[]>('filterInputRef');
const customInputGroupRef = useTemplateRef<FocusableFilterInput>('customInputGroupRef');
const { inputsContainerRef, handleKeydown, focusFirstInput: focusFirstNativeInput } = useArrowNavigation();
const pendingFocus = ref(false);

function focusFirstInput() {
	if (!isDelegate && resolvedFilterConfig.value?.inputGroup) {
		if (customInputGroupRef.value?.focusFirstInput) {
			pendingFocus.value = false;
			customInputGroupRef.value.focusFirstInput();
			return;
		}

		pendingFocus.value = true;
		return;
	}

	const firstFilterInput = Array.isArray(filterInputRef.value) ? filterInputRef.value[0] : filterInputRef.value;

	if (firstFilterInput?.focusFirstInput) {
		pendingFocus.value = false;
		firstFilterInput.focusFirstInput();
		return;
	}

	if (inputsContainerRef.value?.querySelector('input')) {
		pendingFocus.value = false;
		focusFirstNativeInput();
		return;
	}

	pendingFocus.value = true;
}

watch([filterInputRef, customInputGroupRef], () => {
	if (pendingFocus.value) focusFirstInput();
});

defineExpose({ focusFirstInput });

const operator = computed(() => resolvedModel.value?.operator);
const fieldType = computed(() => resolvedPrimitiveField.value.type);

const isNumericField = computed(() => isPrimitiveTypeNumber(fieldType.value) || isPrimitiveTypeDecimal(fieldType.value));

const value = computed<any | any[]>({
	get() {
		if (!resolvedModel.value) return;
		// Re-derive from serialized form for _in/_nin sentinel handling
		const raw = resolvedModel.value.value;
		return ['_in', '_nin'].includes(operator.value ?? '') ? [...((raw as string[]) ?? []).filter((val) => val !== null && val !== ''), null] : raw;
	},
	set(newVal) {
		if (!isEditable.value) return;
		if (!resolvedModel.value) return;
		resolvedModel.value = { ...resolvedModel.value, value: toOperandValue(newVal) };
	},
});

/**
 * Normalizes an edited operand for the current operator. For `_in`/`_nin` an emptied list becomes
 * `undefined` rather than `[]`: `[]` is a complete "in the empty set" filter that matches nothing,
 * while a row the user has cleared is still being authored and has to stay prunable.
 */
function toOperandValue(newVal: any) {
	if (!['_in', '_nin'].includes(operator.value!)) return newVal;

	const entries = (newVal as (string | number)[])
		.flatMap<string | number>((val) => (typeof val === 'string' ? val.split(',') : [val]))
		.filter((val) => !isNil(val) && val !== '');

	return entries.length > 0 ? entries : undefined;
}

const isNullCheck = computed(() => ['_eq', '_neq'].includes(operator.value ?? '') && value.value === null);
const hasInput = computed(() => !isNullCheck.value);

function setValueAt(index: number, newValue: any) {
	if (!isEditable.value) return;
	value.value = set(cloneDeep(value.value), index, newValue);
}

const customInputGroupModel = computed(() =>
	isEditable.value ? model.value : cloneDeep(model.value),
);

function updateCustomInputGroupModel(value: SegmentedFieldFilter | null) {
	if (!isEditable.value) return;
	model.value = value;
}

function removeCustomInputGroup() {
	if (!isEditable.value) return;
	emit('remove');
}

function useArrowNavigation() {
	const inputsContainerRef = useTemplateRef<HTMLElement>('inputsContainerRef');

	function getFilterInputs(): HTMLInputElement[] {
		if (!inputsContainerRef.value) return [];
		return [...inputsContainerRef.value.querySelectorAll('input')];
	}

	function focusInputAt(index: number) {
		const inputs = getFilterInputs();

		if (inputs[index]) {
			inputs[index].focus();
		}
	}

	function getCurrentInputIndex(): number {
		const inputs = getFilterInputs();
		const activeElement = document.activeElement;
		return [...inputs].indexOf(activeElement as HTMLInputElement);
	}

	function focusNextInput() {
		const currentIndex = getCurrentInputIndex();
		focusInputAt(currentIndex + 1);
	}

	function focusPreviousInput() {
		const currentIndex = getCurrentInputIndex();
		focusInputAt(currentIndex - 1);
	}

	function focusFirstInput() {
		focusInputAt(0);
	}

	function focusLastInput() {
		const inputs = getFilterInputs();
		focusInputAt(inputs.length - 1);
	}

	function handleKeydown(event: KeyboardEvent) {
		const target = event.target as HTMLInputElement;

		switch (event.key) {
			case 'ArrowRight':
				if (target.selectionStart === target.value.length) {
					event.preventDefault();
					focusNextInput();
				}

				break;
			case 'ArrowLeft':
				if (target.selectionEnd === 0) {
					event.preventDefault();
					focusPreviousInput();
				}

				break;
			case 'Home':
				event.preventDefault();
				focusFirstInput();
				break;
			case 'End':
				event.preventDefault();
				focusLastInput();
				break;
			case 'Backspace':
				if (target.value.length === 0) {
					event.preventDefault();
					focusPreviousInput();
				}

				break;
		}
	}

	onMounted(() => {
		// Skip auto-focus when a custom inputGroup handles its own focus
		if (!isDelegate && resolvedFilterConfig.value?.inputGroup) return;
		focusFirstInput();
	});

	return {
		inputsContainerRef,
		handleKeydown,
		focusFirstInput,
	};
}
</script>

<template>
	<!-- Custom inputGroup override: render instead of default layout -->
	<component
		:is="resolvedFilterConfig?.inputGroup"
		v-if="!isDelegate && resolvedFilterConfig?.inputGroup"
		ref="customInputGroupRef"
		:model-value="customInputGroupModel"
		:primitive-type="fieldType"
		:type-options="resolvedTypeOptions"
		:disabled="resolvedDisabled"
		:readonly="resolvedReadonly"
		@update:model-value="updateCustomInputGroupModel"
		@remove="removeCustomInputGroup"
	/>

	<!-- Default layout (also used when rendered as delegate) -->
	<div v-else-if="operator && hasInput" ref="inputsContainerRef" @keydown="handleKeydown">
		<div
			v-if="['_eq', '_neq', '_lt', '_gt', '_lte', '_gte', '_contains', '_icontains', '_ncontains', '_nicontains', '_starts_with', '_nstarts_with', '_ends_with', '_nends_with'].includes(operator)"
			class="flex h-full"
		>
			<component
				:is="resolvedFilterInput"
				ref="filterInputRef"
				v-model="value"
				:primitive-type="fieldType"
				:type-options="resolvedTypeOptions"
				:disabled="resolvedDisabled"
				:readonly="resolvedReadonly"
			/>
		</div>
		<div v-else-if="['_in', '_nin'].includes(operator)" class="flex h-full">
			<component
				:is="resolvedFilterInput"
				v-for="(val, index) in value"
				ref="filterInputRef"
				:key="index"
				class="not-last:after:mr-0.5 not-last:after:text-xs not-last:after:content-[',']"
				:class="{ 'not-last:[&_input]:pe-0': !isNumericField }"
				:primitive-type="fieldType"
				:type-options="resolvedTypeOptions"
				:model-value="val"
				:disabled="resolvedDisabled"
				:readonly="resolvedReadonly"
				:small="value.length > 1"
				@update:model-value="setValueAt(Number(index), $event)"
			/>
		</div>
		<div v-else-if="['_between', '_nbetween'].includes(operator)" class="flex h-full items-baseline">
			<component
				:is="resolvedFilterInput"
				ref="filterInputRef"
				:primitive-type="fieldType"
				:type-options="resolvedTypeOptions"
				:model-value="(value as (string | number)[])[0] ?? ''"
				:disabled="resolvedDisabled"
				:readonly="resolvedReadonly"
				small
				@update:model-value="setValueAt(0, $event)"
			/>
			<div class="mx-2 text-xs">
				{{ t('common.and') }}
			</div>
			<component
				:is="resolvedFilterInput"
				ref="filterInputRef"
				:primitive-type="fieldType"
				:type-options="resolvedTypeOptions"
				:model-value="(value as (string | number)[])[1] ?? ''"
				:disabled="resolvedDisabled"
				:readonly="resolvedReadonly"
				small
				@update:model-value="setValueAt(1, $event)"
			/>
		</div>
	</div>
</template>
